import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Sheet, SheetContent } from '../components/ui/sheet';
import {
  Briefcase, Users, Search, BarChart3, Plus, Eye,
  Lock, Star, MapPin, ArrowRight, TrendingUp,
  FileText, Filter, ChevronDown, Building2, Menu, Loader2, LogOut
} from 'lucide-react';


const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  reviewing: 'bg-blue-100 text-blue-700',
  shortlisted: 'bg-purple-100 text-purple-700',
  rejected: 'bg-red-100 text-red-700',
  hired: 'bg-emerald-100 text-emerald-700',
  active: 'bg-emerald-100 text-emerald-700',
  paused: 'bg-gray-100 text-gray-600',
};

export default function RecruiterDashboard() {
  const { user, api, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/'); };
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [jobs, setJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cvSearch, setCvSearch] = useState('');
  const [cvSkills, setCvSkills] = useState('');
  const [cvLocation, setCvLocation] = useState('');
  const isPremium = user?.is_premium || false;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cvResults, setCvResults] = useState([]);
  const [cvTotal, setCvTotal] = useState(0);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvFilters, setCvFilters] = useState({ keywords: '', skills: '', location: '' });
  const [selectedCV, setSelectedCV] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);

  useEffect(() => {
    if (user?.user_type !== 'recruiter' && user?.user_type !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const allowedTabs = ['overview', 'jobs', 'cv_search', 'applicants', 'requests'];
    if (tabParam && allowedTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const jobsRes = await api.get('/jobs/employer');
      setJobs(jobsRes.data?.jobs || jobsRes.data || []);
    } catch {
      setJobs([]);
    }
    try {
      const appsRes = await api.get('/applications?recruiter=me');
      setApplicants(appsRes.data?.applications || appsRes.data || []);
    } catch {
      setApplicants([]);
    }
    try {
      const reqsRes = await api.get('/recruitment/all-requests');
      setRequests(reqsRes.data || []);
    } catch {
      setRequests([]);
    }
    setLoading(false);
  };

  const acceptRequest = async (reqId) => {
    try {
      await api.put(`/recruitment/requests/${reqId}/accept`);
      setRequests(requests.map(r => r.id === reqId ? { ...r, status: 'in_progress' } : r));
      toast.success('Request accepted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to accept request');
    }
  };

  const declineRequest = async (reqId) => {
    try {
      await api.put(`/recruitment/requests/${reqId}/decline`);
      setRequests(requests.filter(r => r.id !== reqId));
      toast.success('Request declined');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to decline request');
    }
  };

  const updateApplicantStatus = async (appId, status) => {
    try {
      await api.put(`/applications/${appId}`, { status });
    } catch {}
    setApplicants(applicants.map(a => a.id === appId ? { ...a, status } : a));
    toast.success('Status updated');
  };

  const searchCVs = async () => {
    setCvLoading(true);
    try {
      const params = new URLSearchParams();
      if (cvFilters.keywords) params.append('keywords', cvFilters.keywords);
      if (cvFilters.skills) params.append('skills', cvFilters.skills);
      if (cvFilters.location) params.append('location', cvFilters.location);
      const res = await api.get(`/cv-search?${params}`);
      setCvResults(res.data.candidates || []);
      setCvTotal(res.data.total || 0);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error(e.response.data.detail || 'Upgrade to search CVs');
      } else {
        toast.error('CV search failed');
      }
    } finally { setCvLoading(false); }
  };

  const viewFullCV = async (userId) => {
    try {
      const res = await api.get(`/cv-search/${userId}/full`);
      setSelectedCV(res.data);
      toast.success('CV unlocked');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to view CV');
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Delete this job posting?')) return;
    try {
      await api.delete(`/jobs/${jobId}`);
    } catch {}
    setJobs(jobs.filter(j => j.id !== jobId));
    toast.success('Job deleted');
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'jobs', label: 'My Jobs', icon: Briefcase },
    { id: 'cv_search', label: 'CV Search', icon: Search },
    { id: 'applicants', label: 'Applicants', icon: Users },
    { id: 'requests', label: 'Requests', icon: Building2 },
  ];

  const totalApps = applicants.length;
  const activeJobs = jobs.filter(j => j.status === 'active').length;
  const totalApplicants = jobs.reduce((sum, j) => sum + (j.applicants || 0), 0);

  const stats = [
    { label: 'Jobs Posted', value: jobs.length, icon: Briefcase, color: 'text-purple-600', bg: 'bg-purple-100' },
    { label: 'Applications Received', value: totalApplicants, icon: Users, color: 'text-blue-600', bg: 'bg-blue-100' },
    { label: 'CVs Searched', value: isPremium ? cvTotal || '—' : '—', icon: Search, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { label: 'Active Jobs', value: activeJobs, icon: TrendingUp, color: 'text-amber-600', bg: 'bg-amber-100' },
  ];

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-purple-800 min-h-screen sticky top-0 hidden lg:block">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">Recruiter</p>
                <p className="text-white/60 text-xs truncate max-w-[140px]">{user?.full_name}</p>
              </div>
            </div>
            <nav className="space-y-1">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  {tab.label}
                  {tab.id === 'cv_search' && !isPremium && (
                    <Lock className="w-3.5 h-3.5 ml-auto text-purple-300" />
                  )}
                </button>
              ))}
            </nav>
            <div className="mt-8">
              <Link to="/employer/post-job">
                <Button className="w-full bg-white text-purple-800 hover:bg-white/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Post Job
                </Button>
              </Link>
              <Button variant="ghost" className="w-full mt-2 text-white/70 hover:text-white hover:bg-white/10" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </aside>

        {/* Mobile Menu Sheet */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-64 p-0 bg-purple-800 border-0">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Briefcase className="w-4 h-4 text-white" />
                </div>
                <p className="text-white font-bold text-sm">Recruiter</p>
              </div>
              <nav className="space-y-1">
                {TABS.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    {tab.label}
                  </button>
                ))}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-white/60 hover:text-white hover:bg-white/5">
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </nav>
            </div>
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 pb-8">
          {/* Mobile header */}
          <div className="lg:hidden flex items-center gap-3 mb-6 pb-4 border-b">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold">
              {TABS.find(t => t.id === activeTab)?.label}
            </h2>
          </div>
          <div className="max-w-5xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Recruiter Dashboard</h1>
                <p className="text-muted-foreground mt-1">Manage jobs, find talent and track applications</p>
              </div>
              <Link to="/employer/post-job" className="hidden md:block">
                <Button className="bg-purple-700 hover:bg-purple-800">
                  <Plus className="w-4 h-4 mr-2" />
                  Post Job
                </Button>
              </Link>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {loading ? (
                    Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24" />)
                  ) : (
                    stats.map((s, i) => (
                      <Card key={i}>
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm text-muted-foreground">{s.label}</p>
                              <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
                            </div>
                            <div className={`w-12 h-12 rounded-xl ${s.bg} flex items-center justify-center`}>
                              <s.icon className={`w-6 h-6 ${s.color}`} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>

                <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold mb-2">Find Top Talent Faster</h3>
                        <p className="text-white/80">
                          Access our database of 26M+ UK candidates. Post jobs and search CVs to find the perfect match.
                        </p>
                      </div>
                      <Link to="/employer/post-job">
                        <Button className="bg-white text-purple-700 hover:bg-white/90 shrink-0">
                          Post a Job
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader><CardTitle>Recent Jobs</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {jobs.slice(0, 3).map(j => (
                          <div key={j.id} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div>
                              <p className="text-sm font-medium">{j.title}</p>
                              <p className="text-xs text-muted-foreground">{j.applicants} applicants</p>
                            </div>
                            <Badge className={statusColors[j.status] || 'bg-gray-100 text-gray-700'}>
                              {j.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader><CardTitle>Recent Applicants</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {applicants.slice(0, 3).map(a => (
                          <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div>
                              <p className="text-sm font-medium">{a.candidate}</p>
                              <p className="text-xs text-muted-foreground">{a.job}</p>
                            </div>
                            <Badge className={statusColors[a.status] || 'bg-gray-100 text-gray-700'}>
                              {a.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* My Jobs Tab */}
            {activeTab === 'jobs' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">My Job Postings ({jobs.length})</h2>
                  <Link to="/employer/post-job">
                    <Button className="bg-purple-700 hover:bg-purple-800">
                      <Plus className="w-4 h-4 mr-2" />
                      Post New Job
                    </Button>
                  </Link>
                </div>
                {jobs.length === 0 ? (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">No jobs posted yet.</p>
                      <Link to="/employer/post-job">
                        <Button className="bg-purple-700 hover:bg-purple-800">Post Your First Job</Button>
                      </Link>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {jobs.map((job) => (
                      <Card key={job.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold truncate">{job.title}</h3>
                                <Badge className={statusColors[job.status] || 'bg-gray-100'}>
                                  {job.status}
                                </Badge>
                              </div>
                              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />{job.location}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />{job.applicants} applicants
                                </span>
                                <span>Posted: {job.posted_at}</span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Link to={`/jobs/${job.id}`}>
                                <Button variant="outline" size="sm">
                                  <Eye className="w-3.5 h-3.5 mr-1" />
                                  View
                                </Button>
                              </Link>
                              <Link to={`/jobs/${job.id}/edit`}>
                                <Button variant="outline" size="sm">Edit</Button>
                              </Link>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-500 border-red-200 hover:bg-red-50"
                                onClick={() => deleteJob(job.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* CV Search Tab */}
            {activeTab === 'cv_search' && (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">CV Search</h2>
                    <p className="text-muted-foreground mt-1">Search {cvTotal > 0 ? cvTotal : 'thousands of'} candidate CVs</p>
                  </div>
                  {!isPremium && (
                    <Badge className="bg-amber-100 text-amber-700 px-3 py-1">Premium Feature — Upgrade to Search</Badge>
                  )}
                </div>

                {/* Filters */}
                <Card>
                  <CardContent className="p-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input value={cvFilters.keywords} onChange={e=>setCvFilters(f=>({...f,keywords:e.target.value}))} placeholder="Job title or keyword..." />
                      <Input value={cvFilters.skills} onChange={e=>setCvFilters(f=>({...f,skills:e.target.value}))} placeholder="Skills (comma-separated)..." />
                      <Input value={cvFilters.location} onChange={e=>setCvFilters(f=>({...f,location:e.target.value}))} placeholder="Location..." />
                    </div>
                    <Button onClick={searchCVs} disabled={cvLoading} className="mt-4 bg-purple-700 hover:bg-purple-800">
                      {cvLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Search className="w-4 h-4 mr-2"/>}
                      Search CVs
                    </Button>
                  </CardContent>
                </Card>

                {/* Results */}
                {cvResults.length > 0 ? (
                  <div className="space-y-3">
                    {cvResults.map((c, idx) => (
                      <Card key={idx} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex gap-4">
                              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                                <span className="text-purple-700 font-bold text-sm">{(c.title||'?').charAt(0).toUpperCase()}</span>
                              </div>
                              <div>
                                <p className="font-semibold">{c.candidate_code}</p>
                                <p className="text-sm text-muted-foreground">{c.title}{c.location ? ` · ${c.location}` : ''}</p>
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {(c.skills||[]).slice(0,6).map(s=><Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
                                </div>
                                {c.summary && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.summary}</p>}
                              </div>
                            </div>
                            <div className="shrink-0 flex flex-col gap-2 items-end">
                              <div className="text-xs text-muted-foreground">{c.experience_count} role{c.experience_count!==1?'s':''} · {c.education_count} edu</div>
                              <Badge className={c.available?'bg-emerald-100 text-emerald-700':'bg-gray-100 text-gray-500'}>{c.available?'Available':'Not Available'}</Badge>
                              <Button size="sm" onClick={()=>viewFullCV(c.user_id)} className="bg-purple-700 hover:bg-purple-800 text-xs">
                                <Eye className="w-3.5 h-3.5 mr-1"/>View Full CV
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : cvLoading ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-700"/></div>
                ) : (
                  <Card><CardContent className="py-12 text-center text-muted-foreground">
                    <Search className="w-12 h-12 mx-auto mb-4 opacity-30"/>
                    <p>Use the filters above to search for candidates</p>
                  </CardContent></Card>
                )}

                {/* Full CV Modal */}
                {selectedCV && (
                  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={()=>setSelectedCV(null)}>
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e=>e.stopPropagation()}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold">{selectedCV.candidate?.full_name || 'Candidate CV'}</h3>
                        <button onClick={()=>setSelectedCV(null)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
                      </div>
                      {selectedCV.candidate && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
                          <p><strong>Email:</strong> {selectedCV.candidate.email}</p>
                          {selectedCV.candidate.phone && <p><strong>Phone:</strong> {selectedCV.candidate.phone}</p>}
                        </div>
                      )}
                      {selectedCV.cv && (
                        <div className="space-y-4 text-sm">
                          {selectedCV.cv.skills?.length > 0 && <div><p className="font-semibold mb-1">Skills</p><div className="flex flex-wrap gap-1">{selectedCV.cv.skills.map(s=><Badge key={s} variant="outline">{s}</Badge>)}</div></div>}
                          {selectedCV.cv.experience?.length > 0 && <div><p className="font-semibold mb-2">Experience</p>{selectedCV.cv.experience.map((e,i)=><div key={i} className="mb-2 p-2 border rounded"><p className="font-medium">{e.title} @ {e.company}</p><p className="text-muted-foreground text-xs">{e.start_date} – {e.end_date||'Present'}</p></div>)}</div>}
                          {selectedCV.cv.education?.length > 0 && <div><p className="font-semibold mb-2">Education</p>{selectedCV.cv.education.map((e,i)=><div key={i} className="mb-2 p-2 border rounded"><p className="font-medium">{e.degree} — {e.institution}</p><p className="text-muted-foreground text-xs">{e.year}</p></div>)}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Applicants Tab */}
            {activeTab === 'applicants' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Applications Received ({applicants.length})</h2>
                <div className="space-y-3">
                  {applicants.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No applications yet.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    applicants.map((app) => (
                      <Card key={app.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-5">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <p className="font-semibold">{app.candidate}</p>
                              <p className="text-sm text-muted-foreground">{app.job}</p>
                              <p className="text-xs text-muted-foreground">Applied: {app.applied_at}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={statusColors[app.status] || 'bg-gray-100 text-gray-700'}>
                                {app.status}
                              </Badge>
                              <select
                                value={app.status}
                                onChange={(e) => updateApplicantStatus(app.id, e.target.value)}
                                className="text-sm border rounded-md px-3 py-1.5 outline-none focus:ring-2 focus:ring-purple-500/20"
                              >
                                <option value="pending">Pending</option>
                                <option value="reviewing">Reviewing</option>
                                <option value="shortlisted">Shortlisted</option>
                                <option value="hired">Hired</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Requests Tab */}
            {activeTab === 'requests' && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold">Recruitment Requests ({requests.length})</h2>
                <div className="space-y-3">
                  {requests.length === 0 ? (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No recruitment requests yet.</p>
                      </CardContent>
                    </Card>
                  ) : (
                    requests.map((req) => (
                      <Card key={req.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedRequest(req); setShowRequestModal(true); }}>
                        <CardContent className="p-5">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold">{req.company_name}</p>
                                <Badge className={req.urgency === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                                  {req.urgency}
                                </Badge>
                                <Badge className={{
                                  pending: 'bg-amber-100 text-amber-700',
                                  in_progress: 'bg-blue-100 text-blue-700',
                                  completed: 'bg-emerald-100 text-emerald-700',
                                }[req.status] || 'bg-gray-100 text-gray-700'}>
                                  {req.status?.replace('_', ' ')}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">{req.job_title} • {req.positions_count} position(s)</p>
                              <p className="text-sm font-medium text-[#003366]">{req.salary_range || 'Salary not specified'}</p>
                              <p className="text-xs text-muted-foreground">Received: {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button className="bg-purple-700 hover:bg-purple-800" size="sm" onClick={(e) => { e.stopPropagation(); acceptRequest(req.id); }}>
                                Accept
                              </Button>
                              <Button variant="outline" size="sm" className="text-red-500 border-red-200" onClick={(e) => { e.stopPropagation(); declineRequest(req.id); }}>
                                Decline
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Recruitment Request Detail Modal */}
            {showRequestModal && selectedRequest && (
              <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Recruitment Request Details</DialogTitle>
                    <DialogDescription>Complete information for recruitment service request</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6">
                    {/* Header Info */}
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <p className="text-sm text-muted-foreground">Job Title</p>
                        <p className="font-semibold text-lg">{selectedRequest.job_title}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Company</p>
                        <p className="font-semibold">{selectedRequest.company_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <Badge className={{
                          pending: 'bg-amber-100 text-amber-700',
                          in_progress: 'bg-blue-100 text-blue-700',
                          completed: 'bg-emerald-100 text-emerald-700',
                        }[selectedRequest.status] || 'bg-gray-100 text-gray-700'}>
                          {selectedRequest.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>

                    {/* Key Details */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Position Details</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground">Location</p>
                          <p className="font-medium">{selectedRequest.location}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Positions</p>
                          <p className="font-medium">{selectedRequest.positions_count}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Salary Range</p>
                          <p className="font-medium">{selectedRequest.salary_range || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Urgency</p>
                          <Badge variant="outline" className="capitalize">{selectedRequest.urgency}</Badge>
                        </div>
                      </div>
                    </div>

                    {/* Job Description */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-2">Job Description</h3>
                      <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedRequest.job_description}</p>
                    </div>

                    {/* Requirements */}
                    {selectedRequest.requirements && selectedRequest.requirements.length > 0 && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-4">Requirements</h3>
                        <ul className="list-disc list-inside space-y-2">
                          {selectedRequest.requirements.map((req, idx) => (
                            <li key={idx} className="text-sm">{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Recruitment Status */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Recruitment Status</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground">Candidates Submitted</p>
                          <p className="font-bold text-2xl">{selectedRequest.candidates_submitted || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Commission</p>
                          <p className="font-bold text-lg">{selectedRequest.fee_percentage}%</p>
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedRequest.notes && (
                      <div className="border-t pt-6">
                        <h3 className="font-semibold mb-2">Additional Notes</h3>
                        <p className="text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">{selectedRequest.notes}</p>
                      </div>
                    )}

                    {/* Timeline */}
                    <div className="border-t pt-6">
                      <h3 className="font-semibold mb-4">Timeline</h3>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-muted-foreground">Submitted</p>
                          <p className="font-medium">{new Date(selectedRequest.created_at).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Last Updated</p>
                          <p className="font-medium">{new Date(selectedRequest.updated_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <Button className="flex-1 bg-purple-700 hover:bg-purple-800" onClick={() => { acceptRequest(selectedRequest.id); setShowRequestModal(false); }}>
                        Accept Request
                      </Button>
                      <Button variant="outline" className="flex-1 text-red-500 border-red-200" onClick={() => { declineRequest(selectedRequest.id); setShowRequestModal(false); }}>
                        Decline Request
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
